import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import type { SignupRole } from '../api/client';
import { Button, TextField, Input, Label } from '@heroui/react';
import { FeatherIcon } from '../components/FeatherIcon';
import { BusyLabel } from '../components/Spinner';
import { AuthShell } from '../brand/AuthShell';
import { Wordmark } from '../brand/Brand';

export function SignupPage() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupRole, setSignupRole] = useState<SignupRole | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!signupRole) {
      setError(t('signup.roleRequired'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('signup.passwordsDoNotMatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('signup.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await signup(email.trim(), password, (username || '').trim() || undefined, signupRole);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signup.signupFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
        <div className="bg-[var(--color-bg-card)]/90 backdrop-blur-xl border border-[var(--color-border-default)] rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8 flex flex-col items-center">
            <h1>
              <Wordmark height={26} label={t('brand')} className="om-auth-wordmark" />
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{t('signup.heading')}</h2>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-1.5" role="radiogroup" aria-label={t('signup.roleLabel')}>
              <span className="text-sm text-[var(--color-text-secondary)]">{t('signup.roleLabel')}</span>
              <div className="grid grid-cols-2 gap-2">
                {(['owner', 'chatter'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    role="radio"
                    aria-checked={signupRole === r}
                    onClick={() => setSignupRole(r)}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                      signupRole === r
                        ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/10'
                        : 'border-[var(--color-border-default)] bg-[var(--color-bg-input)] hover:border-[var(--color-text-tertiary)]'
                    }`}
                  >
                    <span className="block text-sm font-medium text-[var(--color-text-primary)]">{t(`signup.role.${r}.title`)}</span>
                    <span className="block text-xs text-[var(--color-text-tertiary)] mt-0.5">{t(`signup.role.${r}.desc`)}</span>
                  </button>
                ))}
              </div>
            </div>

            <TextField className="space-y-1.5">
              <Label className="text-sm text-[var(--color-text-secondary)]">{t('signup.emailLabel')}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('signup.emailPlaceholder')}
                required
                autoComplete="email"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent-primary)] focus:outline-none transition-colors"
              />
            </TextField>

            <TextField className="space-y-1.5">
              <Label className="text-sm text-[var(--color-text-secondary)]">{t('signup.usernameLabel')}</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('signup.usernamePlaceholder')}
                autoComplete="username"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent-primary)] focus:outline-none transition-colors"
              />
            </TextField>

            <TextField className="space-y-1.5">
              <Label className="text-sm text-[var(--color-text-secondary)]">{t('signup.passwordLabel')}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('signup.passwordPlaceholder')}
                required
                autoComplete="new-password"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent-primary)] focus:outline-none transition-colors"
              />
            </TextField>

            <TextField className="space-y-1.5">
              <Label className="text-sm text-[var(--color-text-secondary)]">{t('signup.confirmPasswordLabel')}</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('signup.confirmPasswordPlaceholder')}
                required
                autoComplete="new-password"
                className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent-primary)] focus:outline-none transition-colors"
              />
            </TextField>

            <Button
              type="submit"
              isDisabled={loading}
              className="w-full py-2.5 rounded-lg bg-[var(--color-accent-primary)] hover:bg-[var(--color-accent-primary)]/90 text-white font-medium transition-colors flex items-center justify-center gap-2"
            >
              <BusyLabel busy={loading} label={t('signup.creatingAccount')}>
                <span className="flex items-center justify-center gap-2">
                  <FeatherIcon name="user-plus" size={16} />
                  {t('signup.createAccount')}
                </span>
              </BusyLabel>
            </Button>

            <p className="text-center text-sm text-[var(--color-text-secondary)]">
              {t('signup.haveAccount')}{' '}
              <Link to="/login" className="text-[var(--color-accent-primary)] hover:underline">
                {t('signup.signInLink')}
              </Link>
            </p>
          </form>
        </div>
    </AuthShell>
  );
}
